import { useEffect, useState } from "react";
import { detectEnvironment, environmentLabel } from "../lib/environment";

/**
 * Persistent "you are not on production" badge.
 *
 * Exists because QA has been split across hypebook.app and hypebookdev.xyz,
 * and a cropped screenshot carries no URL bar — so a bug report couldn't be
 * told apart from a stale build on the other environment. Renders nothing on
 * production.
 *
 * Deliberately unclickable (pointer-events-none) and pinned bottom-left, where
 * it stays clear of the sidebar and toasts. It
 * also prefixes the document title, so the browser tab identifies the
 * environment even when the badge is scrolled out of a screenshot.
 */
export default function EnvironmentBanner() {
  // Hostname is client-only, so this resolves after mount; server render and
  // first paint show nothing rather than guessing wrong.
  const [label, setLabel] = useState<string | null>(null);

  useEffect(() => {
    const next = environmentLabel(detectEnvironment(window.location.hostname));
    setLabel(next);
    if (!next) return;

    const prefix = `[${next}] `;
    const apply = () => {
      if (!document.title.startsWith(prefix)) {
        document.title = prefix + document.title;
      }
    };
    apply();

    // Client-side navigation rewrites <title> from the new route's metadata,
    // which drops the prefix — and inner pages are what actually get
    // screenshotted. Re-apply whenever the title element changes. Guarded by
    // startsWith above, so re-entering from our own write is a no-op.
    const titleEl = document.querySelector("title");
    if (!titleEl) return;
    const observer = new MutationObserver(apply);
    observer.observe(titleEl, { childList: true });
    return () => observer.disconnect();
  }, []);

  if (!label) return null;

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed bottom-2 left-2 z-[9999] select-none rounded-md bg-amber-500 px-2 py-1 font-mono text-[10px] font-bold leading-none tracking-wider text-black shadow-lg ring-1 ring-black/20"
    >
      {label}
    </div>
  );
}
